/** @module @liquidefy/code-editor/settings/settings-manager @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  SettingsScope,
  SettingsProfile,
  SettingsState,
  SettingSchema,
  SettingsSyncConflict,
  SettingsSyncState,
  SettingsChangeEvent,
  SettingsMigration,
  SettingsProfileId,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asSettingsProfileId,
  now,
  emptySettingsState,
  DEFAULT_SETTINGS_SCOPE_ORDER,
} from '../code-editor.js';

// ═══ §2 SETTINGS RESOLUTION — resolveSetting, resolveSettingInScope, getEffectiveSettings ═══

export const SCOPE_PRIORITY: ReadonlyArray<SettingsScope> = DEFAULT_SETTINGS_SCOPE_ORDER;

export function resolveSetting(state: SettingsState, key: string): unknown {
  for (const scope of SCOPE_PRIORITY) {
    const scopeSettings = state.scopes.get(scope);
    if (scopeSettings && key in scopeSettings) return scopeSettings[key];
  }
  const schema = state.schema.get(key);
  return schema ? schema.default : undefined;
}

export function resolveSettingInScope(state: SettingsState, key: string, scope: SettingsScope): unknown {
  const idx = SCOPE_PRIORITY.indexOf(scope);
  if (idx === -1) return undefined;
  for (let i = idx; i >= 0; i--) {
    const scopeSettings = state.scopes.get(SCOPE_PRIORITY[i]!);
    if (scopeSettings && key in scopeSettings) return scopeSettings[key];
  }
  const schema = state.schema.get(key);
  return schema ? schema.default : undefined;
}

export function getEffectiveSettings(state: SettingsState): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, schema] of state.schema) {
    result[key] = schema.default;
  }
  for (const scope of [...SCOPE_PRIORITY].reverse()) {
    const scopeSettings = state.scopes.get(scope);
    if (scopeSettings) {
      for (const [key, value] of Object.entries(scopeSettings)) {
        result[key] = value;
      }
    }
  }
  return result;
}

export function getEffectiveSettingsForScope(state: SettingsState, scope: SettingsScope): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, schema] of state.schema) {
    result[key] = schema.default;
  }
  const idx = SCOPE_PRIORITY.indexOf(scope);
  if (idx === -1) return result;
  for (let i = 0; i <= idx; i++) {
    const scopeSettings = state.scopes.get(SCOPE_PRIORITY[i]!);
    if (scopeSettings) {
      for (const [key, value] of Object.entries(scopeSettings)) {
        result[key] = value;
      }
    }
  }
  return result;
}

export function isOverridden(state: SettingsState, key: string, scope: SettingsScope): boolean {
  const idx = SCOPE_PRIORITY.indexOf(scope);
  if (idx === -1) return false;
  for (let i = idx + 1; i < SCOPE_PRIORITY.length; i++) {
    const scopeSettings = state.scopes.get(SCOPE_PRIORITY[i]!);
    if (scopeSettings && key in scopeSettings) return true;
  }
  return false;
}

export function getOverrideSources(state: SettingsState, key: string): SettingsScope[] {
  const sources: SettingsScope[] = [];
  for (const scope of SCOPE_PRIORITY) {
    const scopeSettings = state.scopes.get(scope);
    if (scopeSettings && key in scopeSettings) {
      sources.push(scope);
    }
  }
  return sources;
}

// ═══ §3 SETTINGS MUTATION — updateSetting, removeSetting, resetSetting, bulkUpdate, importSettings, exportSettings ═══

export function updateSetting(
  state: SettingsState,
  key: string,
  value: unknown,
  scope: SettingsScope
): EditorResult<SettingsState> {
  const schema = state.schema.get(key);
  if (schema) {
    const validation = validateSetting(schema, value);
    if (!validation.success) return validation as unknown as EditorResult<SettingsState>;
  }
  const scopeSettings = state.scopes.get(scope) ?? {};
  const newScopes = new Map(state.scopes);
  newScopes.set(scope, { ...scopeSettings, [key]: value });
  return ok(
    { ...state, scopes: newScopes },
    'SETTING_UPDATED',
    `Setting "${key}" updated in scope "${scope}"`
  );
}

export function removeSetting(
  state: SettingsState,
  key: string,
  scope: SettingsScope
): EditorResult<SettingsState> {
  const scopeSettings = state.scopes.get(scope);
  if (!scopeSettings || !(key in scopeSettings)) {
    return ok(state, 'SETTING_NOT_FOUND', `Setting "${key}" not found in scope "${scope}"`);
  }
  const newSettings = { ...scopeSettings };
  delete newSettings[key];
  const newScopes = new Map(state.scopes);
  newScopes.set(scope, newSettings);
  return ok(
    { ...state, scopes: newScopes },
    'SETTING_REMOVED',
    `Setting "${key}" removed from scope "${scope}"`
  );
}

export function resetSetting(
  state: SettingsState,
  key: string,
  scope: SettingsScope
): EditorResult<SettingsState> {
  return removeSetting(state, key, scope);
}

export function resetAllSettings(
  state: SettingsState,
  scope: SettingsScope
): EditorResult<SettingsState> {
  const newScopes = new Map(state.scopes);
  newScopes.set(scope, {});
  return ok(
    { ...state, scopes: newScopes },
    'SETTINGS_RESET',
    `All settings reset in scope "${scope}"`
  );
}

export function bulkUpdate(
  state: SettingsState,
  settings: Record<string, unknown>,
  scope: SettingsScope
): EditorResult<SettingsState> {
  let current = state;
  for (const [key, value] of Object.entries(settings)) {
    const result = updateSetting(current, key, value, scope);
    if (!result.success) return result;
    current = result.value;
  }
  return ok(current, 'SETTINGS_BULK_UPDATED', `${Object.keys(settings).length} settings updated in scope "${scope}"`);
}

export function importSettings(
  state: SettingsState,
  settings: Record<string, unknown>,
  scope: SettingsScope
): EditorResult<SettingsState> {
  const scopeSettings = state.scopes.get(scope) ?? {};
  const merged = { ...scopeSettings, ...settings };
  const newScopes = new Map(state.scopes);
  newScopes.set(scope, merged);
  return ok(
    { ...state, scopes: newScopes },
    'SETTINGS_IMPORTED',
    `${Object.keys(settings).length} settings imported into scope "${scope}"`
  );
}

export function exportSettings(state: SettingsState, scope: SettingsScope): Record<string, unknown> {
  return { ...(state.scopes.get(scope) ?? {}) };
}

// ═══ §4 SCHEMA VALIDATION — validateSetting, validateString, validateNumber, coerceValue, schemaDescription ═══

export function validateSetting(schema: SettingSchema, value: unknown): EditorResult<boolean> {
  switch (schema.type) {
    case 'string': return validateString(schema, value);
    case 'number': return validateNumber(schema, value);
    case 'boolean': return validateBoolean(schema, value);
    case 'array': return validateArray(schema, value);
    case 'object': return validateObject(schema, value);
    case 'enum': return validateEnum(schema, value);
    default: return ok(true, 'VALID', 'No schema type to validate against');
  }
}

export function validateString(schema: SettingSchema, value: unknown): EditorResult<boolean> {
  if (typeof value !== 'string') {
    return err('INVALID_TYPE', `Expected string, got ${typeof value}`);
  }
  if (schema.pattern) {
    const regex = new RegExp(schema.pattern);
    if (!regex.test(value)) {
      return err('PATTERN_MISMATCH', `Value "${value}" does not match pattern "${schema.pattern}"`);
    }
  }
  return ok(true, 'VALID', 'String value is valid');
}

export function validateNumber(schema: SettingSchema, value: unknown): EditorResult<boolean> {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return err('INVALID_TYPE', `Expected number, got ${typeof value}`);
  }
  if (schema.minimum !== undefined && value < schema.minimum) {
    return err('BELOW_MINIMUM', `Value ${value} is below minimum ${schema.minimum}`);
  }
  if (schema.maximum !== undefined && value > schema.maximum) {
    return err('ABOVE_MAXIMUM', `Value ${value} is above maximum ${schema.maximum}`);
  }
  return ok(true, 'VALID', 'Number value is valid');
}

export function validateBoolean(schema: SettingSchema, value: unknown): EditorResult<boolean> {
  if (typeof value !== 'boolean') {
    return err('INVALID_TYPE', `Expected boolean, got ${typeof value}`);
  }
  return ok(true, 'VALID', 'Boolean value is valid');
}

export function validateArray(schema: SettingSchema, value: unknown): EditorResult<boolean> {
  if (!Array.isArray(value)) {
    return err('INVALID_TYPE', `Expected array, got ${typeof value}`);
  }
  return ok(true, 'VALID', 'Array value is valid');
}

export function validateObject(schema: SettingSchema, value: unknown): EditorResult<boolean> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return err('INVALID_TYPE', `Expected object, got ${Array.isArray(value) ? 'array' : typeof value}`);
  }
  return ok(true, 'VALID', 'Object value is valid');
}

export function validateEnum(schema: SettingSchema, value: unknown): EditorResult<boolean> {
  if (!schema.enum || !Array.isArray(schema.enum)) {
    return err('INVALID_SCHEMA', 'Enum schema must define an "enum" array');
  }
  if (!schema.enum.includes(value)) {
    return err('INVALID_ENUM', `Value ${JSON.stringify(value)} is not in enum: ${JSON.stringify(schema.enum)}`);
  }
  return ok(true, 'VALID', 'Enum value is valid');
}

export function coerceValue(schema: SettingSchema, value: unknown): unknown {
  switch (schema.type) {
    case 'string': {
      if (typeof value === 'number' || typeof value === 'boolean') return String(value);
      return value;
    }
    case 'number': {
      if (typeof value === 'string') {
        const parsed = Number(value);
        if (!Number.isNaN(parsed)) return parsed;
      }
      return value;
    }
    case 'boolean': {
      if (value === 'true' || value === 1) return true;
      if (value === 'false' || value === 0) return false;
      return value;
    }
    case 'array': {
      if (typeof value === 'string') {
        try { return JSON.parse(value); } catch { return value; }
      }
      return value;
    }
    case 'object': {
      if (typeof value === 'string') {
        try { return JSON.parse(value); } catch { return value; }
      }
      return value;
    }
    default:
      return value;
  }
}

export function schemaDescription(schema: SettingSchema): string {
  let desc = `${schema.description} (type: ${schema.type}`;
  if (schema.minimum !== undefined) desc += `, min: ${schema.minimum}`;
  if (schema.maximum !== undefined) desc += `, max: ${schema.maximum}`;
  if (schema.enum) desc += `, values: ${schema.enum.join(' | ')}`;
  if (schema.pattern) desc += `, pattern: ${schema.pattern}`;
  if (schema.deprecationMessage) desc += ` [DEPRECATED: ${schema.deprecationMessage}]`;
  desc += `, default: ${JSON.stringify(schema.default)})`;
  return desc;
}

// ═══ §5 PROFILES — createProfile, deleteProfile, switchProfile, renameProfile, duplicateProfile ═══

export function createProfile(
  state: SettingsState,
  name: string,
  settings?: Record<string, unknown>
): EditorResult<SettingsState> {
  const existing = state.profiles.find(p => p.name === name);
  if (existing) {
    return err('PROFILE_EXISTS', `Profile "${name}" already exists`);
  }
  const profile: SettingsProfile = {
    id: asSettingsProfileId(generateId('profile-')),
    name,
    settings: settings ?? {},
    isDefault: state.profiles.length === 0,
    createdAt: now(),
  };
  const newState: SettingsState = {
    ...state,
    profiles: [...state.profiles, profile],
  };
  return ok(newState, 'PROFILE_CREATED', `Profile "${name}" created`);
}

export function deleteProfile(
  state: SettingsState,
  profileId: SettingsProfileId
): EditorResult<SettingsState> {
  const profile = state.profiles.find(p => p.id === profileId);
  if (!profile) {
    return err('PROFILE_NOT_FOUND', `Profile not found`);
  }
  if (profile.isDefault) {
    return err('CANNOT_DELETE_DEFAULT', 'Cannot delete the default profile');
  }
  const newProfiles = state.profiles.filter(p => p.id !== profileId);
  const newActiveId = state.activeProfileId === profileId ? undefined : state.activeProfileId;
  return ok(
    { ...state, profiles: newProfiles, activeProfileId: newActiveId },
    'PROFILE_DELETED',
    `Profile "${profile.name}" deleted`
  );
}

export function switchProfile(
  state: SettingsState,
  profileId: SettingsProfileId
): EditorResult<SettingsState> {
  const profile = state.profiles.find(p => p.id === profileId);
  if (!profile) {
    return err('PROFILE_NOT_FOUND', `Profile not found`);
  }
  return ok(
    { ...state, activeProfileId: profileId },
    'PROFILE_SWITCHED',
    `Switched to profile "${profile.name}"`
  );
}

export function renameProfile(
  state: SettingsState,
  profileId: SettingsProfileId,
  name: string
): EditorResult<SettingsState> {
  const idx = state.profiles.findIndex(p => p.id === profileId);
  if (idx === -1) {
    return err('PROFILE_NOT_FOUND', `Profile not found`);
  }
  const duplicate = state.profiles.find(p => p.name === name && p.id !== profileId);
  if (duplicate) {
    return err('PROFILE_EXISTS', `Profile "${name}" already exists`);
  }
  const newProfiles = [...state.profiles];
  newProfiles[idx] = { ...newProfiles[idx]!, name };
  return ok(
    { ...state, profiles: newProfiles },
    'PROFILE_RENAMED',
    `Profile renamed to "${name}"`
  );
}

export function duplicateProfile(
  state: SettingsState,
  profileId: SettingsProfileId,
  newName: string
): EditorResult<SettingsState> {
  const source = state.profiles.find(p => p.id === profileId);
  if (!source) {
    return err('PROFILE_NOT_FOUND', `Profile not found`);
  }
  return createProfile(state, newName, { ...source.settings });
}

export function getActiveProfile(state: SettingsState): SettingsProfile | undefined {
  if (!state.activeProfileId) {
    return state.profiles.find(p => p.isDefault);
  }
  return state.profiles.find(p => p.id === state.activeProfileId);
}

export function profileList(state: SettingsState): Array<{ id: SettingsProfileId; name: string; isDefault: boolean; isActive: boolean }> {
  return state.profiles.map(p => ({
    id: p.id,
    name: p.name,
    isDefault: p.isDefault,
    isActive: p.id === state.activeProfileId,
  }));
}

export function exportProfile(state: SettingsState, profileId: SettingsProfileId): EditorResult<Record<string, unknown>> {
  const profile = state.profiles.find(p => p.id === profileId);
  if (!profile) {
    return err('PROFILE_NOT_FOUND', 'Profile not found');
  }
  return ok({ ...profile.settings }, 'PROFILE_EXPORTED', `Profile "${profile.name}" exported`);
}

export function importProfile(
  state: SettingsState,
  name: string,
  settings: Record<string, unknown>
): EditorResult<SettingsState> {
  return createProfile(state, name, settings);
}

// ═══ §6 SYNC — createSyncState, markSynced, startSync, syncConflict, resolveSyncConflict ═══

export function createSyncState(provider: string): SettingsSyncState {
  return {
    provider,
    lastSyncedAt: 0,
    status: 'disabled',
    enabledCategories: [],
    conflicts: [],
  };
}

export function markSynced(state: SettingsSyncState): SettingsSyncState {
  return {
    ...state,
    status: 'synced',
    lastSyncedAt: now(),
    conflicts: [],
  };
}

export function startSync(state: SettingsSyncState): SettingsSyncState {
  return { ...state, status: 'syncing' };
}

export function syncConflict(
  state: SettingsSyncState,
  conflicts: SettingsSyncConflict[]
): SettingsSyncState {
  return {
    ...state,
    status: 'conflict',
    conflicts: [...state.conflicts, ...conflicts],
  };
}

export function resolveSyncConflict(
  state: SettingsSyncState,
  key: string,
  resolution: unknown
): SettingsSyncState {
  const newConflicts = state.conflicts.map(c =>
    c.key === key ? { ...c, resolvedValue: resolution } : c
  );
  const allResolved = newConflicts.every(c => c.resolvedValue !== undefined);
  return {
    ...state,
    conflicts: newConflicts,
    status: allResolved ? 'synced' : 'conflict',
    lastSyncedAt: allResolved ? now() : state.lastSyncedAt,
  };
}

export function pendingConflicts(state: SettingsSyncState): SettingsSyncConflict[] {
  return state.conflicts.filter(c => c.resolvedValue === undefined);
}

export function isSynced(state: SettingsSyncState): boolean {
  return state.status === 'synced';
}

export function syncAge(state: SettingsSyncState): number {
  if (state.lastSyncedAt === 0) return Infinity;
  return now() - state.lastSyncedAt;
}

export function shouldSync(state: SettingsSyncState, interval: number): boolean {
  if (state.status === 'disabled') return false;
  if (state.status === 'syncing') return false;
  if (state.status === 'conflict') return false;
  return syncAge(state) >= interval;
}

// ═══ §7 MIGRATION — applyMigration, needsMigration, migrationChain, applyAllMigrations, validateMigration, migrationPreview ═══

export function applyMigration(
  settings: Record<string, unknown>,
  migration: SettingsMigration
): Record<string, unknown> {
  const result = { ...settings };
  for (const [key, transform] of Object.entries(migration.transforms)) {
    if (key in result) {
      result[key] = transform(result[key]);
    }
  }
  return result;
}

export function needsMigration(settings: Record<string, unknown>, currentVersion: string): boolean {
  const settingsVersion = settings['__version'] as string | undefined;
  if (!settingsVersion) return true;
  return settingsVersion !== currentVersion;
}

export function migrationChain(
  from: string,
  to: string,
  migrations: ReadonlyArray<SettingsMigration>
): SettingsMigration[] {
  const sorted = [...migrations].sort((a, b) => a.fromVersion.localeCompare(b.fromVersion));
  const chain: SettingsMigration[] = [];
  let current = from;

  while (current !== to) {
    const next = sorted.find(m => m.fromVersion === current);
    if (!next) break;
    chain.push(next);
    current = next.toVersion;
  }
  return chain;
}

export function applyAllMigrations(
  settings: Record<string, unknown>,
  from: string,
  to: string,
  migrations: ReadonlyArray<SettingsMigration>
): Record<string, unknown> {
  const chain = migrationChain(from, to, migrations);
  let result = { ...settings };
  for (const migration of chain) {
    result = applyMigration(result, migration);
  }
  result['__version'] = to;
  return result;
}

export function validateMigration(migration: SettingsMigration): EditorResult<boolean> {
  if (!migration.fromVersion || typeof migration.fromVersion !== 'string') {
    return err('INVALID_MIGRATION', 'Migration must have a valid "fromVersion"');
  }
  if (!migration.toVersion || typeof migration.toVersion !== 'string') {
    return err('INVALID_MIGRATION', 'Migration must have a valid "toVersion"');
  }
  if (migration.fromVersion === migration.toVersion) {
    return err('INVALID_MIGRATION', 'Migration "fromVersion" and "toVersion" must differ');
  }
  if (!migration.transforms || typeof migration.transforms !== 'object') {
    return err('INVALID_MIGRATION', 'Migration must have a "transforms" object');
  }
  for (const [key, fn] of Object.entries(migration.transforms)) {
    if (typeof fn !== 'function') {
      return err('INVALID_MIGRATION', `Transform for key "${key}" must be a function`);
    }
  }
  return ok(true, 'MIGRATION_VALID', `Migration from "${migration.fromVersion}" to "${migration.toVersion}" is valid`);
}

export function migrationPreview(
  settings: Record<string, unknown>,
  migration: SettingsMigration
): Array<{ key: string; oldValue: unknown; newValue: unknown }> {
  const migrated = applyMigration(settings, migration);
  const changes: Array<{ key: string; oldValue: unknown; newValue: unknown }> = [];
  for (const key of Object.keys(migration.transforms)) {
    if (key in settings) {
      const oldVal = settings[key];
      const newVal = migrated[key];
      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        changes.push({ key, oldValue: oldVal, newValue: newVal });
      }
    }
  }
  return changes;
}

// ═══ §8 CHANGE TRACKING — diffSettings, hasSettingChanged, changedKeys, settingsChecksum, formatChange ═══

export function diffSettings(
  old: Record<string, unknown>,
  new_: Record<string, unknown>
): SettingsChangeEvent[] {
  const changes: SettingsChangeEvent[] = [];
  const allKeys = new Set([...Object.keys(old), ...Object.keys(new_)]);
  for (const key of allKeys) {
    const oldVal = old[key];
    const newVal = new_[key];
    if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      changes.push({
        key,
        oldValue: oldVal,
        newValue: newVal,
        scope: 'user',
        source: 'user',
      });
    }
  }
  return changes;
}

export function hasSettingChanged(
  old: Record<string, unknown>,
  new_: Record<string, unknown>,
  key: string
): boolean {
  return JSON.stringify(old[key]) !== JSON.stringify(new_[key]);
}

export function changedKeys(
  old: Record<string, unknown>,
  new_: Record<string, unknown>
): string[] {
  const allKeys = new Set([...Object.keys(old), ...Object.keys(new_)]);
  const changed: string[] = [];
  for (const key of allKeys) {
    if (JSON.stringify(old[key]) !== JSON.stringify(new_[key])) {
      changed.push(key);
    }
  }
  return changed;
}

export function settingsChecksum(settings: Record<string, unknown>): string {
  const sorted = Object.keys(settings).sort();
  let hash = 0;
  const str = sorted.map(k => `${k}:${JSON.stringify(settings[k])}`).join('|');
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash + char) | 0;
  }
  return hash.toString(36);
}

export function formatChange(change: SettingsChangeEvent): string {
  const oldStr = change.oldValue === undefined ? '<unset>' : JSON.stringify(change.oldValue);
  const newStr = change.newValue === undefined ? '<unset>' : JSON.stringify(change.newValue);
  return `${change.key}: ${oldStr} -> ${newStr} [${change.scope}]`;
}
